import React, {useEffect, useRef, useState} from 'react';
import './ImageCardWithDetails.scss';
import {Box, Typography} from '@mui/material';
import {ReactComponent as CaretUp} from '../../../assets/images/caret-up.svg';
import {ReactComponent as CaretDown} from '../../../assets/images/caret-down.svg';
import MetadataMenu, {MetadataMenuHandle} from './MetaDataCard';
import {t} from 'i18next';
import ArrButton from '../../../components/common/arr-button/ArrButton';
import {useDocumentDownloadFiles} from '../../../hooks/document/useDocument';

export type ImageCardMetadata = {
  id: string;
  imageUrl: string;
  label: string;
};

type Props = {
  metadata: any;
  imageUrl: string;
  renderCheckbox: ({row}: any) => React.ReactNode;
};

const ImageCardWithDetails: React.FC<Props> = ({
  metadata,
  imageUrl,
  renderCheckbox,
}) => {
  const menuRef = useRef<MetadataMenuHandle>(null);
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [thumbImageUrl, setThumbImageUrl] = useState<string>();
  const [isThumbImageExits, setIsThumbImageExits] =
    useState<boolean>(!!imageUrl);

  const {data: blobData} = useDocumentDownloadFiles({
    filesPath: imageUrl,
  });

  useEffect(() => {
    if (blobData) {
      if (thumbImageUrl) URL.revokeObjectURL(thumbImageUrl);
      const url = URL.createObjectURL(blobData);
      setThumbImageUrl(url);
    }
  }, [blobData]);

  useEffect(() => {
    return () => {
      if (thumbImageUrl) {
        URL.revokeObjectURL(thumbImageUrl);
      }
    };
  }, [thumbImageUrl]);

  const handleDetailsClick = (e: React.MouseEvent<HTMLElement>) => {
    menuRef.current?.open(e.currentTarget);
    setIsMenuOpen(true);
  };

  return (
    <Box className="card">
      <Box className="thumbnail-container">
        {isThumbImageExits ? (
          <img
            src={thumbImageUrl}
            onError={() => {
              setIsThumbImageExits(false);
            }}
            alt={t('docList.grid.thumbnail')}
            className="thumbnail"
            data-testid={`card-image-${metadata.DocID}`}
          />
        ) : (
          <Typography
            variant="h6"
            className="typo-text body3_size alt_text"
            data-testid={`image-card-with-details-no-thumbnail`}
          >
            {t('docList.grid.noThumbnail')}
          </Typography>
        )}
      </Box>
      <Box className="bottom-bar">
        <Box className="button">
          <ArrButton
            variant="secondary"
            title={t('docList.grid.details')}
            size="xs"
            endIcon={isMenuOpen ? <CaretUp /> : <CaretDown />}
            dataTestId={`detail_button-${metadata.DocID}`}
            className="detail_button"
            onClick={handleDetailsClick}
          >
            {t('docList.grid.details')}
          </ArrButton>
        </Box>
        <Box className="checkbox" data-testid={`row-${metadata.DocID}`}>
          {renderCheckbox({row: metadata})}
        </Box>
      </Box>
      <MetadataMenu
        ref={menuRef}
        onClose={() => setIsMenuOpen(false)}
        metadata={metadata}
      />
    </Box>
  );
};

export default ImageCardWithDetails;
